import React, { useMemo } from "react";
import { Card, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { GET_REWARDS_TRANSFERS } from "../../../graphql/transaction";
import { formatCashFull } from "../../../utils/formatCash";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import TransactionsType from "../../../enums/TransactionsType";
import { fullName } from "utils/fullName";

const REDEEMED_TYPES: string[] = [
  TransactionsType.REWARDS_TO_BANK,
  TransactionsType.REWARDS_TO_BALANCE,
  TransactionsType.OUTBOUND_REWARDS,
];

interface Data {
  id: string;
  date: string;
  amount: string;
  description: string;
}

interface RewardsHistoryTableProps {
  refreshDataIndex: number;
}

const RewardsHistoryTable: React.FC<RewardsHistoryTableProps> = (props) => {
  const { refreshDataIndex } = props;
  const { t } = useTranslation();

  const extractRewardHistory = (data: any): Data[] => {
    const firstKey = Object.keys(data)[0];
    return data[firstKey]?.edges.map((edge: any) => {
      const userName = fullName(edge.node.user) || null;
      const type = REDEEMED_TYPES.includes(edge.node.type)
        ? t("Redeemed rewards")
        : t("Earned");
      const memo = edge.node.memo;
      const toAccount = edge.node.merchant;

      return {
        id: edge.node.id,
        date: new Date(edge.node.transactionDate).toLocaleDateString(),
        amount: formatCashFull(edge.node.amount, edge.node.currency),
        description: `${userName ? userName : ""} ${
          userName ? type.toLowerCase() : type
        } ${toAccount !== "OUTPAVE_MERCHANT" ? `to ${toAccount}` : ""}${memo ? ` with memo '${memo}'` : ""}`,
      };
    });
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_REWARDS_TRANSFERS,
      initialVariables: { first: DEFAULT_PAGINATION_SIZE, after: undefined },
      extractData: extractRewardHistory,
      refetchIndex: refreshDataIndex,
    });

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      { id: "date", header: t("Date"), cell: (row) => row.date },
      { id: "amount", header: t("Amount"), cell: (row) => row.amount },
      {
        id: "description",
        header: t("Description"),
        cell: (row) => row.description,
      },
    ],
    [t],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        minWidth: 0,
        padding: "var(--space-5)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h4">{t("Rewards History")}</Typography>
      <DataTable<Data>
        columns={columns}
        rows={dataArray}
        getRowKey={(row) => row.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onScroll={handleScroll}
        maxHeight={350}
        emptyState={t("No reward history found")}
        loadingMoreText={t("Loading more rewards history")}
        stickyHeader
      />
    </Card>
  );
};

export default RewardsHistoryTable;
